import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Users, Sparkles, Upload, Trash2, Loader2 } from 'lucide-react';
import { getLeadGroups, deleteLeadGroup, type LeadGroup } from '@/lib/lead-groups';
import { useToast } from '@/hooks/useToast';
import { UploadModal } from '@/components/UploadModal';
import { AIGenerateModal } from '@/components/AIGenerateModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LeadListAvatar } from '@/components/LeadListAvatar';

function sourceBadge(source: string) {
  const colors: Record<string, string> = {
    ai: 'bg-violet-500/10 text-violet-500',
    csv: 'bg-blue-500/10 text-blue-500',
    manual: 'bg-base-content/10 text-base-content/50',
  };
  const labels: Record<string, string> = { ai: 'AI', csv: 'CSV', manual: 'Manual' };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[source] || colors.manual}`}>
      {labels[source] || source}
    </span>
  );
}

export function LeadGroupPicker({ currentGroupId, onSelect }: { currentGroupId?: string; onSelect?: (groupId: string) => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [groups, setGroups] = useState<LeadGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  async function loadGroups() {
    try {
      const data = await getLeadGroups();
      data.sort((a, b) => {
        const aSent = a.sentCount > 0 && a.sentCount >= a.leadCount ? 1 : 0;
        const bSent = b.sentCount > 0 && b.sentCount >= b.leadCount ? 1 : 0;
        return aSent - bSent;
      });
      setGroups(data);
    } catch {
      toast.error('Failed to load lead lists');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteGroup(id: string, name: string) {
    try {
      await deleteLeadGroup(id);
      setGroups((prev) => prev.filter((g) => g.id !== id));
      toast.success(`Deleted "${name}"`);
    } catch {
      toast.error('Failed to delete group');
    }
  }

  function handleAiComplete(data: { leads: any[]; count: number; leadGroupId: string }) {
    toast.success(`Generated ${data.count} leads`);
    setShowAiModal(false);
    if (data.leadGroupId) {
      handleSelect(data.leadGroupId);
    } else {
      loadGroups();
    }
  }

  function handleSelect(groupId: string) {
    if (onSelect) {
      onSelect(groupId);
    } else {
      navigate(`/draft-emails`);
    }
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('selectedGroupId', groupId);
    }
  }

  const totalLeads = groups.reduce((sum, g) => sum + g.leadCount, 0);

  return (
    <div className="flex flex-col gap-[50px] animate-fade-in">
      {/* Action buttons */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-bold tracking-tight">Add New Leads</h2>
          <p className="text-xs text-base-content/40 mt-0.5">Generate leads with AI or import from a file</p>
        </div>
        <div className="flex items-center gap-3 justify-center">
        <button onClick={() => setShowAiModal(true)}
          className="w-[220px] h-[68px] rounded-xl px-4 py-3 border-l-4 border-l-primary glass-card shadow-sm transition-all hover:shadow-md cursor-pointer flex items-center gap-2 text-left">
          <Sparkles size={18} className="text-primary shrink-0" />
          <span className="text-sm font-semibold">Find Leads with AI</span>
        </button>
        <button onClick={() => setShowUploadModal(true)}
          className="w-[220px] h-[68px] rounded-xl px-4 py-3 border-l-4 border-l-base-content/20 glass-card shadow-sm transition-all hover:shadow-md cursor-pointer flex items-center gap-2 text-left">
          <Upload size={18} className="text-base-content/50 shrink-0" />
          <span className="text-sm font-semibold">Upload Lead List (CSV)</span>
        </button>
        </div>
      </div>

      <div>
        <h2 className="text-base font-bold tracking-tight">Select a Lead List</h2>
        <p className="text-xs text-base-content/40 mt-0.5">
          {groups.length} lead list{groups.length !== 1 ? 's' : ''} &middot; {totalLeads} lead{totalLeads !== 1 ? 's' : ''}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-primary" />
        </div>
      ) : groups.length === 0 ? (
        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => setShowAiModal(true)}
            className="glass-card rounded-xl p-8 border-2 border-dashed border-base-300/50 hover:border-primary/30 transition-colors flex flex-col items-center justify-center gap-3 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Sparkles size={20} className="text-primary" />
            </div>
            <p className="text-sm font-medium">Find Leads with AI</p>
            <p className="text-xs text-base-content/40">Tell us your target audience</p>
          </button>
          <button onClick={() => setShowUploadModal(true)}
            className="glass-card rounded-xl p-8 border-2 border-dashed border-base-300/50 hover:border-primary/30 transition-colors flex flex-col items-center justify-center gap-3 text-center cursor-pointer">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Upload size={20} className="text-primary" />
            </div>
            <p className="text-sm font-medium">Upload Lead List (CSV)</p>
            <p className="text-xs text-base-content/40">Upload a CSV or Excel file</p>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => {
            const isSelected = group.id === currentGroupId;
            return (
              <div
                key={group.id}
                onClick={() => handleSelect(group.id)}
                className={`glass-card rounded-xl p-4 group relative animate-fade-in hover:shadow-md transition-shadow cursor-pointer min-h-[68px] flex items-center overflow-hidden ${
                  isSelected ? 'ring-2 ring-primary/40' : ''
                }`}
              >
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget({ id: group.id, name: group.name }); }}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs text-base-content/20 hover:text-error z-10"
                >
                  <Trash2 size={12} />
                </button>

                <div className="flex items-center gap-3 min-w-0 w-full overflow-hidden">
                  <LeadListAvatar seed={group.name} size={36} />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="text-sm font-semibold overflow-hidden whitespace-nowrap">{group.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {sourceBadge(group.source)}
                      <span className="text-[10px] text-base-content/40">{group.leadCount} lead{group.leadCount !== 1 ? 's' : ''}</span>
                      {isSelected && <span className="text-[10px] text-primary font-medium">Selected</span>}
                      {group.sentCount > 0 && group.sentCount >= group.leadCount ? (
                        <span className="text-[10px] text-green-500 font-medium">&#10003; Done</span>
                      ) : group.sentCount > 0 ? (
                        <span className="text-[10px] text-green-500/70">{group.sentCount}/{group.leadCount}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AIGenerateModal open={showAiModal} onClose={() => setShowAiModal(false)} onComplete={handleAiComplete} />
      <UploadModal open={showUploadModal} onClose={() => setShowUploadModal(false)} onFileSelect={() => {}} />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete lead list?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? All leads in this group will be removed.`}
        onConfirm={() => { if (deleteTarget) handleDeleteGroup(deleteTarget.id, deleteTarget.name); setDeleteTarget(null); }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
